import { AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { API_HEADERS } from '../../constants/api-constants';
import { getActiveTenantId, getValidMaiaAccessToken } from './brokerSession';

// Shared axios request hook: attach the broker-minted Maia JWT as the bearer for
// every Maia backend (maia-api, rules-api, user-api).
//
// The token's `tenant_id` is authoritative. Callers must switch tenants
// (ensureTenant) before issuing a tenant-scoped request, so a request carrying
// an `X-Tenant-Id` for a *different* tenant than the active token is a state
// bug — either the wrong token is active or the caller passed the wrong tenant.
// Dropping the mismatched header would let the backend silently answer for the
// token's tenant, surfacing an unexpected tenant's data as if it were the one
// requested (looks like a leak, even though the token is authorized for it). So
// fail loudly here instead of silently "correcting" the tenant.
export async function attachMaiaAuth(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  const headers = (config.headers ??= new AxiosHeaders());

  const token = await getValidMaiaAccessToken();
  if (token) {
    headers.set(API_HEADERS.AUTHORIZATION, `Bearer ${token}`);
  }

  const activeTenant = getActiveTenantId();
  const requestedTenant = headers.get(API_HEADERS.TENANT_ID);
  if (activeTenant && requestedTenant && requestedTenant !== activeTenant) {
    throw new Error(
      `Tenant mismatch: request targets "${String(requestedTenant)}" but the ` +
        `active Maia token is scoped to "${activeTenant}". Switch tenants ` +
        `(ensureTenant) before calling, or correct the X-Tenant-Id.`,
    );
  }

  return config;
}
